import { dehydrate, QueryClient } from '@tanstack/react-query';
import { PromotionV2 as PromotionV2Page } from 'components/pages/PromotionV2';
import {
  fetchPromotionV2,
  PROMOTION_V2_PAGE_KEY,
} from 'hooks/pages/usePromotionV2';
import { fetchPromotionsV2 } from 'hooks/usePromotionsV2';
import { env } from 'lib/constants/env';
import type { PageContext } from 'types/common/PageContext';
import {
  blocksBuildTimeResolver,
  prefetchCommonData,
  prefetchReviewsData,
} from 'utils/buildTime';
import { fetchWithSentry } from 'utils/pageTemplates/fetchWithSentry';

export function promotionV2Page() {
  async function getStaticProps(context: PageContext) {
    const queryClient = new QueryClient();

    const promotionPageData = await fetchWithSentry({
      queryClient,
      queryKey: [PROMOTION_V2_PAGE_KEY, context.params.slug],
      queryFn: fetchPromotionV2,
      tags: {
        template: 'promotionV2Page',
        slug: context.params.slug,
        api: 'sanity',
      },
    });

    if (!promotionPageData) {
      return {
        notFound: true,
        revalidate: env.ISR_TTL_SECONDS,
      };
    }

    await prefetchCommonData(queryClient);
    await prefetchReviewsData(queryClient);
    await blocksBuildTimeResolver(queryClient, promotionPageData.blocks);

    return {
      revalidate: env.ISR_TTL_SECONDS,
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  const getStaticPaths = async () => {
    const promotions = await fetchPromotionsV2();
    const paths = promotions.map((promotion) => ({
      params: { slug: promotion.slug },
    }));

    return { paths, fallback: 'blocking' as const };
  };

  const PromotionV2PageComponent = () => <PromotionV2Page />;

  return {
    getStaticProps,
    getStaticPaths,
    PromotionV2PageComponent,
  };
}
